import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, MousePointer, RotateCcw } from "lucide-react";
import { cn } from "@/lib/utils";
import { Odometer } from "@/components/ui/odometer";
import salaryCalc from "@/lib/calculators/us-salary";
import HeroDemoCTA from "@/components/calculator/HeroDemoCTA";

// ============================================================
// FYUNOR — Hero Calculator Preview
// A compact, animated product demo that tells one short story:
// "Enter salary → choose state → get take-home pay → use the full
// calculator." Runs once on load, then rests on a CTA.
//
// Uses the REAL US Salary Calculator logic for the take-home result
// (no hard-coded values). CSS transforms/opacity only — no animation
// libraries. The calculator UI is non-interactive (pointer-events-none,
// select-none, aria-hidden); only the CTA link and replay button are
// interactive.
// ============================================================

const TYPING_STEPS = ["$", "$8", "$85", "$85,", "$85,0", "$85,00", "$85,000"];
const STATE_OPTIONS_DEMO = [
  { value: "CA", label: "California" },
  { value: "TX", label: "Texas" },
  { value: "NY", label: "New York" },
  { value: "FL", label: "Florida" },
];
const formatMonthly = (v) => `$${Math.round(v).toLocaleString("en-US")}`;

export default function HeroCalculatorPreview() {
  const [reducedMotion] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
  const [finePointer] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(pointer: fine)").matches
  );
  const showCursor = finePointer && !reducedMotion;

  // Real take-home pay for $85,000 / single / California (monthly).
  const finalMonthly = useMemo(() => {
    const result = salaryCalc.calculate({ annualSalary: 85000, state: "CA", filingStatus: "single" });
    return result.primary.value / 12;
  }, []);

  // Animation state
  const [demoKey, setDemoKey] = useState(0);
  const [entered, setEntered] = useState(false);
  const [cursorVisible, setCursorVisible] = useState(false);
  const [cursorTarget, setCursorTarget] = useState(null);
  const [salaryFocused, setSalaryFocused] = useState(false);
  const [salaryText, setSalaryText] = useState("");
  const [stateFocused, setStateFocused] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [stateValue, setStateValue] = useState("");
  const [calculated, setCalculated] = useState(false);
  const [takeHomeValue, setTakeHomeValue] = useState(0);
  const [resultHighlighted, setResultHighlighted] = useState(false);
  const [showCTA, setShowCTA] = useState(false);
  const [cursorPos, setCursorPos] = useState({ x: 400, y: 16 });

  const containerRef = useRef(null);
  const salaryRef = useRef(null);
  const stateRef = useRef(null);
  const caOptionRef = useRef(null);

  // ---- Main timeline (runs once per demoKey) ----
  useEffect(() => {
    if (reducedMotion) {
      setEntered(true);
      setShowCTA(true);
      return;
    }
    const timers = [];
    const at = (fn, ms) => timers.push(setTimeout(fn, ms));

    at(() => setEntered(true), 50);

    if (showCursor) {
      at(() => setCursorVisible(true), 1000);
      at(() => { setCursorTarget("salary"); setSalaryFocused(true); }, 1200);
    } else {
      at(() => setSalaryFocused(true), 1200);
    }

    TYPING_STEPS.forEach((step, i) => at(() => setSalaryText(step), 1200 + i * 150));

    if (showCursor) {
      at(() => { setCursorTarget("state"); setSalaryFocused(false); setStateFocused(true); }, 2300);
      at(() => setDropdownOpen(true), 2500);
      at(() => setCursorTarget("ca"), 2700);
      at(() => { setStateValue("California"); setDropdownOpen(false); }, 2900);
    } else {
      at(() => setStateValue("California"), 2500);
    }

    at(() => setCalculated(true), 3000);
    at(() => setTakeHomeValue(finalMonthly), 3050);
    at(() => setResultHighlighted(true), 3100);
    at(() => setResultHighlighted(false), 3900);
    at(() => setShowCTA(true), 4800);

    return () => timers.forEach(clearTimeout);
  }, [demoKey, reducedMotion, showCursor, finalMonthly]);

  // ---- Cursor position (measured from real element rects) ----
  useEffect(() => {
    if (!showCursor || !containerRef.current) return;
    if (cursorTarget === null) {
      setCursorPos({ x: containerRef.current.offsetWidth - 70, y: 16 });
      return;
    }
    const el = { salary: salaryRef, state: stateRef, ca: caOptionRef }[cursorTarget]?.current;
    if (!el) return;
    const cRect = containerRef.current.getBoundingClientRect();
    const eRect = el.getBoundingClientRect();
    setCursorPos({
      x: eRect.left - cRect.left + eRect.width * 0.3,
      y: eRect.top - cRect.top + eRect.height * 0.5,
    });
  }, [cursorTarget, showCursor]);

  const handleReplay = () => {
    setEntered(false);
    setCursorVisible(false);
    setCursorTarget(null);
    setSalaryFocused(false);
    setSalaryText("");
    setStateFocused(false);
    setDropdownOpen(false);
    setStateValue("");
    setCalculated(false);
    setTakeHomeValue(0);
    setResultHighlighted(false);
    setShowCTA(false);
    setDemoKey((k) => k + 1);
  };

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative mx-auto w-full max-w-lg rounded-2xl p-4 shadow-lg transition-all duration-700 ease-out md:mx-0 md:ml-auto md:mr-[calc(26px_-_max(0px,(100vw_-_1600px)/2))] lg:mr-[calc(68px_-_max(0px,(100vw_-_1600px)/2))] lg:p-5",
        entered ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"
      )}
      style={{
        background:
          "linear-gradient(135deg, hsl(213 100% 96%) 0%, hsl(217 98% 92%) 45%, hsl(220 100% 87%) 100%)",
      }}
    >
      <div className="relative grid min-h-[260px]">
        {/* ---- Calculator demo (non-interactive, aria-hidden) ---- */}
        <div
          aria-hidden="true"
          className={cn(
            "col-start-1 row-start-1 select-none rounded-xl bg-surface p-5 pointer-events-none transition-opacity duration-700",
            showCTA ? "opacity-0" : "opacity-100"
          )}
        >
          <h3 className="font-heading text-base font-semibold tracking-[var(--tracking-heading)] text-foreground">
            US Salary Calculator
          </h3>

          {/* Annual Salary */}
          <div className="mt-3 flex flex-col gap-1">
            <span className="font-body text-xs font-medium leading-none text-muted-foreground">
              Annual Salary
            </span>
            <div
              ref={salaryRef}
              className={cn(
                "flex items-center border-b py-1.5 transition-colors duration-300",
                salaryFocused ? "border-primary" : "border-border"
              )}
            >
              <span className="min-h-[1.25rem] font-mono tabular text-base text-foreground">
                {salaryText}
              </span>
            </div>
          </div>

          {/* State */}
          <div className="relative mt-2 flex flex-col gap-1">
            <span className="font-body text-xs font-medium leading-none text-muted-foreground">
              State
            </span>
            <div
              ref={stateRef}
              className={cn(
                "flex items-center justify-between border-b py-1.5 transition-colors duration-300",
                stateFocused ? "border-primary" : "border-border"
              )}
            >
              <span className={cn("font-body text-base", stateValue ? "text-foreground" : "text-subtle-foreground")}>
                {stateValue || "Select state"}
              </span>
              <ChevronDown className="size-3.5 text-muted-foreground" aria-hidden="true" />
            </div>
            {dropdownOpen && (
              <div className="absolute left-0 right-0 top-full z-30 mt-1 rounded-lg bg-surface p-1 shadow-lg ring-1 ring-border">
                {STATE_OPTIONS_DEMO.map((opt) => (
                  <div
                    key={opt.value}
                    ref={opt.value === "CA" ? caOptionRef : undefined}
                    className={cn(
                      "flex items-center rounded-md px-3 py-1.5 font-body text-sm",
                      cursorTarget === "ca" && opt.value === "CA"
                        ? "bg-primary/10 text-primary"
                        : "text-foreground"
                    )}
                  >
                    {opt.label}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="my-3 border-t border-border" />

          {/* Estimated Take-home Pay */}
          <div>
            <span className="font-body text-xs font-medium uppercase leading-none tracking-wide text-subtle-foreground">
              Estimated Take-home Pay
            </span>
            <div className="mt-1 flex items-baseline gap-1.5">
              {calculated ? (
                <Odometer
                  value={takeHomeValue}
                  format={formatMonthly}
                  duration={600}
                  className={cn(
                    "font-mono text-2xl font-bold tabular transition-colors duration-500 sm:text-3xl",
                    resultHighlighted ? "text-primary" : "text-foreground"
                  )}
                />
              ) : (
                <span className="font-mono text-2xl font-bold tabular text-subtle-foreground sm:text-3xl">—</span>
              )}
              <span className="font-body text-xs text-muted-foreground">/ month</span>
            </div>
          </div>
        </div>

        {/* ---- CTA (on gradient, crossfades in) ---- */}
        <div
          className={cn(
            "col-start-1 row-start-1 flex items-center p-5 transition-opacity duration-700",
            showCTA ? "opacity-100" : "pointer-events-none opacity-0"
          )}
        >
          <HeroDemoCTA focusable={showCTA} />
        </div>
      </div>

      {/* ---- Decorative cursor (desktop / fine pointer only) ---- */}
      {showCursor && (
        <MousePointer
          className="absolute left-0 top-0 z-40 size-4 fill-surface text-foreground"
          style={{
            transform: `translate(${cursorPos.x}px, ${cursorPos.y}px)`,
            opacity: cursorVisible ? 1 : 0,
            transition: "transform 400ms cubic-bezier(0, 0, 0.2, 1), opacity 300ms ease-out",
            filter: "drop-shadow(0 1px 2px rgb(0 0 0 / 0.18))",
          }}
          aria-hidden="true"
        />
      )}

      {/* ---- Replay button ---- */}
      <button
        type="button"
        onClick={handleReplay}
        aria-label="Replay demo"
        className="absolute bottom-3 right-3 z-50 flex size-8 items-center justify-center rounded-full bg-surface/80 text-muted-foreground shadow-sm backdrop-blur-sm transition-colors hover:text-foreground"
      >
        <RotateCcw className="size-4" aria-hidden="true" />
      </button>
    </div>
  );
}